export interface AskNewAiRequest {
  messages: { role: 'user' | 'assistant'; content: string }[];
  context?: {
    userId?: string;
    organizationId?: string;
    pathname?: string;
    pageContext?: any;
    currentModule?: string;
    forceAppHelp?: boolean;
    forceIntent?: 'app_help';
    selectedLocationId?: string;
    locationIds?: string[];
    [key: string]: any;
  };
}

export interface DisambiguationOption {
  id: string;
  label: string;
}

export interface Disambiguation {
  type: 'location';
  question: string;
  options: DisambiguationOption[];
}

export interface AskNewAiResponse {
  reply: string;
  route?: any;
  plan?: any;
  bag?: any;
  disambiguation?: Disambiguation;
}

export interface ProgressEvent {
  step: 'starting' | 'routing' | 'planning' | 'retrieving' | 'composing' | 'complete' | 'error';
  message: string;
  data?: any;
  timestamp: number;
}

export type ProgressCallback = (event: ProgressEvent) => void;

// Helper function to detect if we're running on localhost
const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.')
  );
};

// Automatically select URL based on environment
const getAskNewAiUrl = (): string => {
  const envAny = (import.meta as any).env || {};
  const directUrl: string | undefined = envAny.VITE_ASK_NEW_AI_URL;
  
  // If explicit URL is set in env, use it
  if (directUrl) return directUrl;
  
  // Otherwise, auto-detect based on hostname
  if (isLocalhost()) {
    return 'http://localhost:5001/lemur-476302/us-central1/askNewAi';
  }
  
  // Production URL
  return 'https://us-central1-lemur-476302.cloudfunctions.net/askNewAi';
};

/**
 * Send AI message with SSE streaming for progressive feedback
 */
export function sendAskNewAiMessageStreaming(
  req: AskNewAiRequest,
  onProgress: ProgressCallback
): Promise<AskNewAiResponse> {
  return new Promise((resolve, reject) => {
    const envAny = (import.meta as any).env || {};
    const base: string = envAny.VITE_FUNCTIONS_BASE || '/api';
    // Prioritize directUrl first, then auto-detected URL, then base path
    const prioritized = [envAny.VITE_ASK_NEW_AI_URL, getAskNewAiUrl(), `${base}/askNewAi`].filter(Boolean) as string[];
    const baseUrl = prioritized[0];
    
    // Build URL with stream parameter
    const url = new URL(baseUrl);
    url.searchParams.set('stream', 'true');
    
    // Use fetch with streaming for SSE
    fetch(url.toString(), {
      method: 'POST',
      mode: 'cors',
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(req),
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`HTTP ${response.status}: ${text}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent: string | null = null;

        // Client-side pacing: buffer events and replay with timing
        const progressQueue: ProgressEvent[] = [];
        let isPlaying = false;
        let isComplete = false;

        // Replay buffered progress events with pacing
        const playNext = (): void => {
          if (isComplete || !progressQueue.length) {
            isPlaying = false;
            return;
          }

          isPlaying = true;
          const step = progressQueue.shift()!;
          
          // Render the step immediately
          onProgress(step);
          
          // Schedule next step (400ms feels good for pacing)
          setTimeout(playNext, 400);
        };

        // Add progress event to queue and start playing if not already
        const queueProgress = (event: ProgressEvent): void => {
          progressQueue.push(event);
          if (!isPlaying && !isComplete) {
            playNext();
          }
        };

        // Flush remaining queue immediately (for complete/error)
        const flushQueue = (): void => {
          isComplete = true;
          while (progressQueue.length > 0) {
            const step = progressQueue.shift()!;
            onProgress(step);
          }
        };

        const processChunk = async (): Promise<void> => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                // Flush any remaining progress events
                flushQueue();
                
                // Connection closed - check if we have a complete response
                if (buffer.trim()) {
                  // Try to parse any remaining data
                  const lines = buffer.split('\n');
                  for (const line of lines) {
                    if (line.startsWith('data: ')) {
                      try {
                        const jsonData = line.substring(6);
                        const data = JSON.parse(jsonData);
                        if (data.reply) {
                          resolve(data as AskNewAiResponse);
                          return;
                        }
                      } catch (e) {
                        // Ignore parse errors
                      }
                    }
                  }
                }
                
                // Stream ended unexpectedly - provide a user-friendly error
                const errorMessage = 'The connection was interrupted before receiving a complete response. This might be due to a network issue or server timeout. Please try again.';
                reject(new Error(errorMessage));
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              
              // Process complete SSE messages
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                
                if (!line || line.startsWith(':')) continue; // Skip empty lines and comment lines
                
                if (line.startsWith('event: ')) {
                  currentEvent = line.substring(7).trim();
                } else if (line.startsWith('data: ')) {
                  const jsonData = line.substring(6);
                  try {
                    const data = JSON.parse(jsonData);
                    
                    if (currentEvent === 'progress') {
                      // Queue progress events for paced replay
                      queueProgress(data as ProgressEvent);
                    } else if (currentEvent === 'complete') {
                      // Flush queue and resolve immediately
                      flushQueue();
                      resolve(data as AskNewAiResponse);
                      return;
                    } else if (currentEvent === 'error') {
                      // Flush queue and reject immediately
                      flushQueue();
                      reject(new Error(data.error || 'Unknown error'));
                      return;
                    } else if (!currentEvent && data.reply) {
                      // Fallback: if no event type but has reply, treat as complete
                      flushQueue();
                      resolve(data as AskNewAiResponse);
                      return;
                    }
                  } catch (e) {
                    console.error('Error parsing SSE data:', e, 'Data:', jsonData);
                  }
                  currentEvent = null; // Reset after processing
                }
              }
            }
          } catch (error) {
            flushQueue();
            reject(error);
          }
        };

        await processChunk();
      })
      .catch((error) => {
        console.error('askNewAiService streaming error:', error);
        reject(error);
      });
  });
}

/**
 * Send AI message (non-streaming, backward compatible)
 */
export async function sendAskNewAiMessage(req: AskNewAiRequest): Promise<AskNewAiResponse> {
  const envAny = (import.meta as any).env || {};
  const base: string = envAny.VITE_FUNCTIONS_BASE || '/api';
  // Prioritize directUrl first, then auto-detected URL, then base path
  const prioritized = [envAny.VITE_ASK_NEW_AI_URL, getAskNewAiUrl(), `${base}/askNewAi`].filter(Boolean) as string[];
  const url = prioritized[0];
  
  try {
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(req),
    });
    
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${text}`);
    }
    
    const data = await res.json();
    return data as AskNewAiResponse;
  } catch (e: any) {
    console.error('askNewAiService error:', e);
    console.warn('askNewAiService endpoint:', url);
    return {
      reply: `Temporary response (service not connected). Please configure VITE_FUNCTIONS_BASE or VITE_ASK_NEW_AI_URL.\n\nYour question: ${req.messages[req.messages.length - 1]?.content}`,
    };
  }
}
